import { isNil, startCase } from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { theme } from 'antd';

import EPChartDonut from '@/comp/common/EPChartDonut';
import { InsightType } from '@/constants/enums';
import { ActionBreakdown } from '@/store/InsightsStore';
import { emptyValue } from '@/store/LeakageStore';
import { formatThousands } from '@/utils/util-functions';

import ActionCountDetails from './ActionCountDetails';
import KPISkeleton from '@/comp/KPIBox/KPISkeleton/KPISkeleton';

const { useToken } = theme;

const getDonutChartMappings = token => [
  { name: InsightType.Risk, colors: [token.colorError, token.colorErrorBorder] },
  { name: InsightType.Maintenance, colors: [token.colorWarning, token.colorWarningBorder] },
  { name: InsightType.Energy, colors: [token.colorSuccess, token.colorSuccessBorder] },
];

interface TopSectionProps {
  total: {
    insightsCount: number;
    totalValue: number;
    currency: string;
    risk: ActionBreakdown;
    maintenance: ActionBreakdown;
    energy: ActionBreakdown;
    rejectedAmount: number;
  };
  animation?: boolean;
  isLoading?: boolean;
}

const TopSection = ({ total, animation, isLoading }: TopSectionProps) => {
  const { t } = useTranslation();
  const { token } = useToken();

  return (
    <div className="flex-row">
      <div className="flex-row">
        <span className="title">
          {`${t('total')} `} <span className="rejected-title">{`(${t('rejected')})`}</span>
        </span>
        <div className="lifetime-action-count-details">
          <ActionCountDetails
            actionCount={(total?.insightsCount ?? 0) + (total?.rejectedAmount ?? 0)}
            value={`${isNil(total?.totalValue) ? emptyValue : formatThousands(total?.totalValue)} ${
              total?.currency ?? ''
            }`}
            label={t('lifetimeValue')}
            actionCountClassName="font-size-big"
            chipClassName="background-color-grey"
            rejectedAmount={total?.rejectedAmount ?? 0}
            isLoading={isLoading}
          />
        </div>
      </div>
      <div className="donut-charts">
        {getDonutChartMappings(token).map(section => (
          <KPISkeleton
            loading={isLoading}
            withSliderSkeleton={false}
            key={section.name}
            width={80}
            height={80}
            style={{ marginBottom: token.marginMD }}
          >
            {!isNil(total?.[section.name]) && total?.[section.name]?.total ? (
              <EPChartDonut
                animation={animation}
                innerTitle={total[section.name].total}
                bottomTitle={t(startCase(section.name))}
                data={[
                  {
                    name: t('actionsTaken'),
                    value: total[section.name].actionsTaken,
                    color: section.colors[0],
                  },
                  {
                    name: t('actionsPending'),
                    value: total[section.name].actionsPending,
                    color: section.colors[1],
                  },
                ]}
              />
            ) : null}
          </KPISkeleton>
        ))}
      </div>
    </div>
  );
};

export default TopSection;
